// src/components/StockInItems.js
import React, { useState, useEffect } from 'react';
import Sidebar from '../components/Sidebar'; // adjust path
import {
  FaDownload,
  FaPlus,
  FaEdit,
  FaTrash,
  FaEye,
  FaSort,
  FaSortUp,
  FaSortDown,
  FaChevronLeft,
  FaChevronRight,
} from 'react-icons/fa';
import StockInFormModal from './StockInFormModal'; // Import the modal component
import StockInViewModal from './StockInViewModal';
import PDFDesigner from './pdfDesigner';
import axios from 'axios';
import { useScreenPermissions } from '../components/useScreenPermissions';
import { useNavigate } from 'react-router-dom';

const API_URL = '/api';

const StockInItems = () => {
  // Keep the original StockIn theme colors but map to a local COLORS object for layout parity
  const COLORS = {
    CLOUD: '#F5EFE6',   // page background (theme.pageBg)
    BABY: '#F0ECE2',    // header / table header (theme.headerBg)
    BABY1: '#B88A5A',   // primary / active (theme.primary)
    DIVIDER: '#E6D9CC', // divider / border (theme.border)
    BORDER: '#E6D9CC',  // inputs & card borders
    TEXT: '#211f1eff',    // main text (theme.textDark)
    MUTED: '#6E584A',   // muted text (theme.textMuted)
    BADGE_BG: '#F6EFE4',// badge background (theme.badgeBg)
    SUCCESS_BG: '#EAF3EE',
    WARNING_BG: '#FBF2E8',
    PRIMARY_HOVER: '#A27246'
  };

  // --- THEME CSS VARIABLES FOR SIDEBAR & GLOBAL THEME (Stock In page) ---
  useEffect(() => {
    const themeVars = {
      '--sidebar-line-color': COLORS.BORDER,
      '--sidebar-bg': '#ffffff',
      '--page-background': COLORS.CLOUD,
      '--accent-color': COLORS.BABY1,
      '--accent-hover': COLORS.PRIMARY_HOVER,
      '--page-border-color': COLORS.BORDER,
      '--text-primary': COLORS.TEXT,
      '--text-muted': COLORS.MUTED,
      '--text-muted-2': COLORS.MUTED,
      '--disabled-color': '#C9BFB3',
      '--search-icon': COLORS.MUTED,
      '--hover-gray': COLORS.BADGE_BG
    };

    Object.entries(themeVars).forEach(([k, v]) => {
      document.documentElement.style.setProperty(k, v);
    });
    // no cleanup to allow other pages to override
  }, []);

  // ----------------- Inject scoped CSS overrides (sharp edges + table dividers) -----------------
  useEffect(() => {
    const styleId = 'stockin-currencymaster-overrides';
    if (document.getElementById(styleId)) return;

    const style = document.createElement('style');
    style.id = styleId;
    style.innerHTML = `
      /* Scope overrides to the StockIn page root */
      .stockin-root .rounded-md,
      .stockin-root .rounded-lg,
      
      .stockin-root .rounded-xl,
      .stockin-root .rounded-sm,
      

      .stockin-root input[type="text"],
      .stockin-root select,
      .stockin-root .shadow,
      .stockin-root .shadow-sm,
      .stockin-root .rounded-md,
      

      .stockin-root .shadow-lg,
      .stockin-root .shadow-sm,
      .stockin-root .shadow {
        box-shadow: 0 1px 2px rgba(15,23,42,0.04) !important;
      }


      .stockin-root .sticky.top-0.shadow-lg {
        box-shadow: 0 1px 0 rgba(15,23,42,0.06) !important;
      }

      .stockin-root tr:hover { background-color: #faf9f8 !important; }

      
      .stockin-root input[type="text"], .stockin-root select {
        background: #ffffff !important;
        border: 1px solid ${COLORS.BORDER} !important;
        color: ${COLORS.TEXT} !important;
      }

      /* full-width row divider using pseudo element so actions column also has divider */
      .stockin-root table { border-collapse: separate; border-spacing: 0; width: 100%; }
      .stockin-root table thead th { background-clip: padding-box; }
      .stockin-root table tbody tr { position: relative; }
      .stockin-root table tbody tr::after {
        content: "";
        position: absolute;
        left: 0;
        right: 0;
        height: 1px;
        background: ${COLORS.DIVIDER};
        bottom: 0;
        pointer-events: none;
      }
      .stockin-root table tbody td { border-bottom: none; position: relative; z-index: 1; background-clip: padding-box; }
      .stockin-root table thead th.sticky { position: sticky; top: 0; z-index: 20; }

      /* ---------- Rounded badges (status / inventory) ---------- */
      .stockin-root .status-badge,
      .stockin-root .inventory-badge {
        border-radius: 9999px !important;
        display: inline-flex !important;
        align-items: center;
        justify-content: center;
        line-height: 1;
        padding-top: 0.25rem !important;
        padding-bottom: 0.25rem !important;
        padding-left: 0.75rem !important;
        padding-right: 0.75rem !important;
        box-shadow: none !important;
        border: none !important;
      }
     .stock-status{
          border-radius: 16px;
    padding: 4px 10px;
          font-size: 12px;
          font-weight:600;
          display: inline-block;
          }

          .stock-status-in{
         border-radius: 16px;
        padding: 4px 10px;
          font-size: 12px;
            font-weight:600;
          display: inline-block;
          }


      /* ---------- Force sidebar profile avatar circular on StockIn page ---------- */
      .stockin-root .sidebar .profile,
      .stockin-root .sidebar .profile-icon,
      .stockin-root .sidebar .profile-img,
      .stockin-root .sidebar .avatar,
      .stockin-root [data-sidebar] .profile,
      .stockin-root [data-sidebar] .avatar,
      .stockin-root aside .profile,
      .stockin-root aside .avatar,
      .stockin-root aside img.profile-img {
        border-radius: 9999px !important;
        overflow: hidden !important;
      }
    `;
    document.head.appendChild(style);
  }, []);

  // Force-apply avatar circular style if Sidebar renders with different classnames or inline styles
  useEffect(() => {
    const applyAvatarStyles = () => {
      // possible sidebar containers
      const sidebarSelectors = [
        '.stockin-root aside',
        '.stockin-root nav',
        '.stockin-root .sidebar',
        '.stockin-root [data-sidebar]',
        '.stockin-root aside .sidebar',
      ];
      let sidebarEl = null;
      for (const sel of sidebarSelectors) {
        sidebarEl = document.querySelector(sel);
        if (sidebarEl) break;
      }
      if (!sidebarEl) return;

      // Find images inside sidebar
      const imgs = sidebarEl.querySelectorAll('img');
      imgs.forEach(img => {
        // apply circular styling
        img.style.borderRadius = '9999px';
        img.style.overflow = 'hidden';
        img.style.objectFit = 'cover';
        // optionally ensure width/height remain same as original, don't force size
      });

      // Find divs/spans used as avatar (initials / background)
      const avatarCandidates = sidebarEl.querySelectorAll('[class*="profile"], [class*="avatar"], [data-avatar], .user-avatar, .profile-icon, .profile-img, .avatar');
      avatarCandidates.forEach(el => {
        // If it's not an <img>, we still force the rounding and overflow
        if (el && el.nodeType === 1) {
          el.style.borderRadius = '9999px';
          el.style.overflow = 'hidden';
          // if element has background image or bg color, this will round it visually
        }
      });
    };

    // Initial try
    applyAvatarStyles();

    // Observe sidebar for dynamic updates (reapply when sidebar content changes)
    const root = document.querySelector('.stockin-root');
    if (!root) return;
    const sidebarNode = root.querySelector('aside, nav, .sidebar, [data-sidebar]');
    const observerTarget = sidebarNode || root;
    const mo = new MutationObserver(() => {
      // small debounce
      setTimeout(() => applyAvatarStyles(), 50);
    });
    mo.observe(observerTarget, { childList: true, subtree: true, attributes: true });
    // cleanup
    return () => mo.disconnect();
  }, []);

  // --- local state (kept same as original) ---
  const [searchTerm, setSearchTerm] = useState('');
  const [status, setStatus] = useState('All');
  const [inventoryFilter, setInventoryFilter] = useState('All');
  const [stockIns, setStockIns] = useState([]);
  const [isPDFDesignerOpen, setIsPDFDesignerOpen] = useState(false);
  const [selectedStockinForPDF, setSelectedStockinForPDF] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [selectedStockIn, setSelectedStockIn] = useState(null);
  const [viewModalOpen, setViewModalOpen] = useState(false);
   const navigate = useNavigate();
  const { hasPermission, hasFullAccess, loading: permissionsLoading } = useScreenPermissions();
  const [stats, setStats] = useState({
    totalStockIns: 0,
    withInventoryFlag: 0,
    withoutInventoryFlag: 0,
    createdThisMonth: 0
  });
  const [sidebarCollapsed, setSidebarCollapsed] = useState(true);

  // Sorting & pagination
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(6);

  // Fetch stock in records
  useEffect(() => {
    fetchStockIns();
  }, []);

  const downloadStockinPDF = async (stockinNumber) => {
    try {
      const response = await axios.get(`${API_URL}/designs/Stock%20in/pdf/stockin/${stockinNumber}`, {
        responseType: 'arraybuffer'
      });

      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `Stk_in_${stockinNumber}.pdf`;
      link.click();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading PDF:', error);
      alert(`Failed to download PDF: ${error.message}`);
    }
  };

  const fetchStockIns = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_URL}/stockin`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      if (!response.ok) throw new Error('Failed to fetch stock in records');
      const data = await response.json();
      setStockIns(Array.isArray(data) ? data : []);
      calculateStats(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Error fetching stock in records:', error);
      alert('Error loading stock in records');
      setStockIns([]);
    } finally {
      setLoading(false);
    }
  };

  const calculateStats = (stockInData) => {
    const now = new Date();
    const currentMonth = now.getMonth();
    const currentYear = now.getFullYear();

    let totalStockIns = stockInData.length || 0;
    let withInventoryFlag = 0;
    let withoutInventoryFlag = 0;
    let createdThisMonth = 0;

    (stockInData || []).forEach(stockIn => {
      if (stockIn.InventoryFlag === true || stockIn.InventoryFlag === 1) withInventoryFlag++;
      else withoutInventoryFlag++;

      const stockInDate = new Date(stockIn.StockInDate);
      if (stockInDate.getMonth() === currentMonth && stockInDate.getFullYear() === currentYear) createdThisMonth++;
    });

    setStats({ totalStockIns, withInventoryFlag, withoutInventoryFlag, createdThisMonth });
  };

    const handleLogout = () => {
    navigate('/login');
  };

  const handleDownloadCSV = () => {
    let csvContent = "Stock In ID,Date,Added By,Total Quantity,Note,Status,Inventory Flag\n";
    (stockIns || []).forEach(stockIn => {
      const statusText = stockIn.Status === 1 || stockIn.Status === true ? 'Active' : 'Inactive';
      const inventoryFlag = stockIn.InventoryFlag === 1 || stockIn.InventoryFlag === true ? 'Yes' : 'No';
      csvContent += `${stockIn.StockInID},${stockIn.StockInDate},${stockIn.AddedByUserName},${stockIn.TotalQuantity},${stockIn.Note || ''},${statusText},${inventoryFlag}\n`;
    });

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', 'stock_in_records.csv');
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleCreate = () => {
    setSelectedStockIn(null);
    setShowModal(true);
  };
  
  const handleEdit = (stockIn) => {
    setSelectedStockIn(stockIn);
    setShowModal(true);
  };
  
  const handleView = (stockIn) => {
    setSelectedStockIn(stockIn);
    setViewModalOpen(true);
  };

  const handleDelete = async (stockInId) => {
    if (window.confirm(`Are you sure you want to delete Stock In ${stockInId}?`)) {
      try {
        const response = await fetch(`${API_URL}/stockin/${stockInId}`, {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        });
        
        if (!response.ok) throw new Error('Failed to delete stock in record');
        
        // Refresh the list
        fetchStockIns();
        alert('Stock in record deleted successfully');
      } catch (error) {
        console.error('Error deleting stock in record:', error);
        alert('Error deleting stock in record');
      }
    }
  };
  
 const handleModalSubmit = () => {
    // Refresh the list after form submission
    fetchStockIns();
  };

  // toggle status
  const toggleStockInStatus = async (stockInId, currentStatus) => {
    try {
      const newStatus = currentStatus ? 0 : 1;
      const response = await fetch(`${API_URL}/stockin/${stockInId}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ ...selectedStockIn, Status: newStatus })
      });
      if (!response.ok) throw new Error('Failed to update stock in status');
      fetchStockIns();
      alert(`Stock in record ${newStatus ? 'activated' : 'deactivated'} successfully`);
    } catch (error) {
      console.error('Error updating stock in status:', error);
      alert(`Error updating stock in status: ${error.message}`);
    }
  };

  // sorting logic (same as original)
  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') direction = 'desc';
    setSortConfig({ key, direction });
  };

  const getSortedData = (data) => {
    if (!sortConfig.key) return data;
    return [...data].sort((a, b) => {
      let aValue, bValue;
      switch (sortConfig.key) {
        case 'StockInID':
        case 'AddedByUserName':
          aValue = (a[sortConfig.key] || '').toString().toLowerCase();
          bValue = (b[sortConfig.key] || '').toString().toLowerCase();
          break;
        case 'StockInDate':
          aValue = new Date(a[sortConfig.key] || 0);
          bValue = new Date(b[sortConfig.key] || 0);
          break;
        case 'TotalQuantity':
          aValue = Number(a[sortConfig.key] || 0);
          bValue = Number(b[sortConfig.key] || 0);
          break;
        case 'Status':
        case 'InventoryFlag':
          aValue = a[sortConfig.key] ? 1 : 0;
          bValue = b[sortConfig.key] ? 1 : 0;
          break;
        default:
          aValue = a[sortConfig.key] || '';
          bValue = b[sortConfig.key] || '';
      }
      if (aValue < bValue) return sortConfig.direction === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });
  };

  // filtering
  const filteredStockIns = stockIns.filter(stockIn => {
    const matchesSearch = 
      stockIn.StockInID.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (stockIn.AddedByUserName && stockIn.AddedByUserName.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (stockIn.Note && stockIn.Note.toLowerCase().includes(searchTerm.toLowerCase()));
    const isActive = stockIn.Status === 1 || stockIn.Status === true;
    const matchesStatus = status === 'All' ||
      (status === 'Active' && isActive) ||
      (status === 'Inactive' && !isActive);

    const hasInventoryFlag = stockIn.InventoryFlag === 1 || stockIn.InventoryFlag === true;
    const matchesInventoryFlag = inventoryFilter === 'All' ||
      (inventoryFilter === 'Yes' && hasInventoryFlag) ||
      (inventoryFilter === 'No' && !hasInventoryFlag);

    return matchesSearch && matchesStatus && matchesInventoryFlag;
  });

  const sortedStockIns = getSortedData(filteredStockIns);

  // pagination
  const totalPages = Math.ceil(sortedStockIns.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentItems = sortedStockIns.slice(startIndex, endIndex);

  useEffect(() => {
    console.log('Current page:', currentPage);
    console.log('Items per page:', itemsPerPage);
    console.log('Total items:', sortedStockIns.length);
    console.log('Current items:', currentItems.length);
  }, [currentPage, sortedStockIns, currentItems]);

  // sort arrows
  const renderSortArrows = (columnName) => {
    const isSorted = sortConfig.key === columnName;
    const isAsc = isSorted && sortConfig.direction === 'asc';
    const isDesc = isSorted && sortConfig.direction === 'desc';

    return (
      <span className="ml-1 inline-flex flex-col">
        <span className={`text-xs ${isAsc ? 'font-bold' : ''}`} style={{ color: isAsc ? COLORS.TEXT : COLORS.MUTED }}>
          <FaSortUp />
        </span>
        <span className="text-xs -mt-1" style={{ color: isDesc ? COLORS.TEXT : COLORS.MUTED, marginTop: '-0.45rem' }}>
          <FaSortDown />
        </span>
      </span>
    );
  };

  const handlePageChange = (page) => setCurrentPage(page);
  useEffect(() => {
    console.log('Current page:', currentPage);
    console.log('Items per page:', itemsPerPage);
    console.log('Total items:', sortedStockIns.length);
    console.log('Current items:', currentItems.length);
  }, [currentPage, sortedStockIns, currentItems]);

  // pagination buttons styled to match StockOut layout but using StockIn tokens
  const renderPaginationButtons = () => {
    const pages = [];
    const startPage = Math.max(1, currentPage - 2);
    const endPage = Math.min(totalPages, currentPage + 2);

    pages.push(
      <button
        key="prev"
        className={`p-2 transition-all duration-200`}
        onClick={() => handlePageChange(currentPage - 1)}
        disabled={currentPage === 1}
        style={currentPage === 1 ? { color: '#9CA3AF', cursor: 'not-allowed' } : { color: COLORS.TEXT }}
        aria-label="Previous"
      >
        <FaChevronLeft className="w-4 h-4" />
      </button>
    );

    for (let i = startPage; i <= endPage; i++) {
      const isActive = currentPage === i;
      pages.push(
        <button
          key={i}
          className={`px-3 py-1 font-medium transition-all duration-200 ${isActive ? '' : 'hover:bg-gray-100'}`}
          onClick={() => handlePageChange(i)}
          style={isActive ? { backgroundColor: COLORS.BABY1, color: '#fff' } : { color: COLORS.TEXT }}
        >
          {i}
        </button>
      );
    }

    pages.push(
      <button
        key="next"
        className={`p-2 transition-all duration-200`}
        onClick={() => handlePageChange(currentPage + 1)}
        disabled={currentPage === totalPages || totalPages === 0}
        style={currentPage === totalPages || totalPages === 0 ? { color: '#9CA3AF', cursor: 'not-allowed' } : { color: COLORS.TEXT }}
        aria-label="Next"
      >
        <FaChevronRight className="w-4 h-4" />
      </button>
    );

    return pages;
  };

  // permissions
  const canCreate = hasFullAccess || hasPermission('MRN_MASTER', 'CanCreate');
  const canView = hasFullAccess || hasPermission('MRN_MASTER', 'CanView');
  const canEdit = hasFullAccess || hasPermission('MRN_MASTER', 'CanEdit');
  const canDelete = hasFullAccess || hasPermission('MRN_MASTER', 'CanDelete');

  if (permissionsLoading) {
    return (
      <div className="min-h-screen flex justify-center items-center" style={{ backgroundColor: COLORS.CLOUD }}>
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 mx-auto" style={{ borderTopColor: COLORS.BABY, borderBottomColor: COLORS.BABY }} />
          <p className="mt-4" style={{ color: COLORS.MUTED }}>Loading permissions...</p>
        </div>
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', background: COLORS.CLOUD }} className="flex stockin-root">
      <Sidebar
        collapsed={sidebarCollapsed}
        onToggle={setSidebarCollapsed}
        theme={{ sidebarBg: '#ffffff', activeBg: COLORS.BABY1, activeText: '#ffffff', profileBg: COLORS.BABY1 }}
        onLogout={handleLogout}
      />
      <main className={`flex-1 p-6 lg:p-8 overflow-hidden transition-all duration-300 ${sidebarCollapsed ? 'lg:ml-[90px]' : 'lg:ml-[280px]'}
`}>

        {/* Header */}
        <div className="mb-8 border-b" style={{ borderColor: COLORS.BORDER, paddingBottom: 12 }}>
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
            <div>
              <h1 className="text-3xl font-semibold" style={{ color: COLORS.TEXT }}>Stock In Records</h1>
              <p className="mt-1" style={{ color: COLORS.MUTED }}>Manage your stock in records here.</p>
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={handleDownloadCSV}
                className="flex items-center gap-2 px-4 py-2 bg-white border font-medium transition-all duration-200 shadow-sm"
                style={{ borderColor: COLORS.BORDER, color: COLORS.TEXT }}
              >
                <FaDownload className="w-4 h-4" />
                Export
              </button>
              {canCreate && (
                <button
                  onClick={handleCreate}
                  className="flex items-center gap-2 px-4 py-2 font-medium transition-all duration-200 shadow-sm"
                  style={{ backgroundColor: COLORS.BABY1, color: '#fff' }}
                  onMouseEnter={e => e.currentTarget.style.backgroundColor = COLORS.PRIMARY_HOVER}
                  onMouseLeave={e => e.currentTarget.style.backgroundColor = COLORS.BABY1}
                >
                  <FaPlus className="w-4 h-4" />
                  Create New
                </button>
              )}
              <button
                onClick={() => { if (currentItems[0]) { setSelectedStockinForPDF(currentItems[0]); setIsPDFDesignerOpen(true); } }}
                className="flex items-center gap-2 px-4 py-2 font-medium transition-all duration-200 shadow-sm"
                style={{ backgroundColor: COLORS.BABY1, color: '#fff' }}
                onMouseEnter={e => e.currentTarget.style.backgroundColor = COLORS.PRIMARY_HOVER}
                onMouseLeave={e => e.currentTarget.style.backgroundColor = COLORS.BABY1}
              >
                <FaEdit className="w-4 h-4" /> Design PDF
              </button>
            </div>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <div className="p-5 bg-white border" style={{ borderColor: COLORS.BORDER }}>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium" style={{ color: COLORS.MUTED }}>Total Stock Ins</p>
                <p className="text-2xl font-bold" style={{ color: COLORS.TEXT }}>{stats.totalStockIns}</p>
              </div>
              <div className="flex-shrink-0 flex items-center justify-center w-10 h-10 bg-slate-100" style={{ color: COLORS.TEXT }}>
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" /></svg>
              </div>
            </div>
          </div>

          <div className="p-5 bg-white border" style={{ borderColor: COLORS.BORDER }}>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium" style={{ color: COLORS.MUTED }}>With Inventory Flag</p>
                <p className="text-2xl font-bold" style={{ color: COLORS.TEXT }}>{stats.withInventoryFlag}</p>
              </div>
              <div className="flex-shrink-0 flex items-center justify-center w-10 h-10 bg-teal-50" style={{ color: COLORS.TEXT }}>
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
              </div>
            </div>
          </div>

          <div className="p-5 bg-white border" style={{ borderColor: COLORS.BORDER }}>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium" style={{ color: COLORS.MUTED }}>Without Inventory Flag</p>
                <p className="text-2xl font-bold" style={{ color: COLORS.TEXT }}>{stats.withoutInventoryFlag}</p>
              </div>
              <div className="flex-shrink-0 flex items-center justify-center w-10 h-10 bg-amber-50" style={{ color: COLORS.TEXT }}>
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
              </div>
            </div>
          </div>

          <div className="p-5 bg-white border" style={{ borderColor: COLORS.BORDER }}>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium" style={{ color: COLORS.MUTED }}>Created This Month</p>
                <p className="text-2xl font-bold" style={{ color: COLORS.TEXT }}>{stats.createdThisMonth}</p>
              </div>
              <div className="flex-shrink-0 flex items-center justify-center w-10 h-10 bg-slate-100" style={{ color: COLORS.TEXT }}>
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg>
              </div>
            </div>
          </div>
        </div>

        {/* Search & Filter */}
        <div className="p-5 bg-white mb-6 border" style={{ borderColor: COLORS.BORDER }}>
          <div className="flex flex-col md:flex-row gap-3 items-end">
            <div className="flex-1">
              <label className="block text-sm font-semibold mb-1" style={{ color: COLORS.MUTED }}>Search</label>
              <div className="relative">
                {/* keep layout consistent with StockOut: use an icon left if desired in child CSS */}
                <input
                  type="text"
                  placeholder="Search Stock In ID, Added By, Note, etc."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-3 pr-4 py-2 bg-white border text-slate-900 focus:outline-none transition-all duration-200"
                  style={{ borderColor: COLORS.BORDER }}
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-semibold mb-1" style={{ color: COLORS.MUTED }}>Status</label>
              <select
                value={status}
                onChange={(e) => setStatus(e.target.value)}
                className="px-4 py-2 bg-white border text-slate-900 focus:outline-none transition-all duration-200"
                style={{ borderColor: COLORS.BORDER }}
              >
                <option value="All">All</option>
                <option value="Active">Active</option>
                <option value="Inactive">Inactive</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-semibold mb-1" style={{ color: COLORS.MUTED }}>Inventory Flag</label>
              <select
                value={inventoryFilter}
                onChange={(e) => setInventoryFilter(e.target.value)}
                className="px-4 py-2 bg-white border text-slate-900 focus:outline-none transition-all duration-200"
                style={{ borderColor: COLORS.BORDER }}
              >
                <option value="All">All</option>
                <option value="Yes">Yes</option>
                <option value="No">No</option>
              </select>
            </div>

            <button
              className="px-5 py-2 font-medium transition-all duration-200 shadow-sm"
              onClick={() => fetchStockIns()}
              style={{ backgroundColor: COLORS.BABY1, color: '#fff' }}
              onMouseEnter={e => e.currentTarget.style.backgroundColor = COLORS.PRIMARY_HOVER}
              onMouseLeave={e => e.currentTarget.style.backgroundColor = COLORS.BABY1}
            >
              Search
            </button>
          </div>
        </div>

        {/* Table */}
        <section className="bg-white border" style={{ borderColor: COLORS.BORDER }}>
          <div className="flex justify-between items-center p-4 border-b" style={{ borderColor: COLORS.BORDER }}>
            <h2 className="text-xl font-semibold" style={{ color: COLORS.TEXT }}>Stock In Records</h2>
            <div className="flex items-center space-x-2">
              <div className="flex items-center space-x-1 text-gray-500">
                {renderPaginationButtons()}
              </div>
            </div>
          </div>

          <div className="h-full overflow-x-auto">
            {loading ? (
              <div className="text-center py-8">
                <p className="text-slate-600">Loading stock in records...</p>
              </div>
            ) : sortedStockIns.length === 0 ? (
              <div className="text-center py-8">
                <p className="text-slate-500">No stock in records found</p>
              </div>
            ) : (
              <table className="w-full custom-table" style={{ borderCollapse: 'separate' }}>
                <thead className="text-xs font-semibold text-left uppercase text-gray-800 " style={{ backgroundColor: COLORS.BABY,  borderBottom: `2px solid ${COLORS.DIVIDER}` }}>
                  <tr>
                    <th className="px-4 py-3 cursor-pointer" onClick={() => handleSort('StockInID')}>
                      <div className="flex items-center">Stock In ID {renderSortArrows('StockInID')}</div>
                    </th>
                    <th className="px-4 py-3 cursor-pointer" onClick={() => handleSort('StockInDate')}>
                      <div className="flex items-center">Date {renderSortArrows('StockInDate')}</div>
                    </th>
                    <th className="px-4 py-3 cursor-pointer" onClick={() => handleSort('AddedByUserName')}>
                      <div className="flex items-center">Added By {renderSortArrows('AddedByUserName')}</div>
                    </th>
                    <th className="px-4 py-3 cursor-pointer" onClick={() => handleSort('TotalQuantity')}>
                      <div className="flex items-center">Total Quantity {renderSortArrows('TotalQuantity')}</div>
                    </th>
                    <th className="px-4 py-3">Note</th>
                    <th className="px-4 py-3 cursor-pointer" onClick={() => handleSort('Status')}>
                      <div className="flex items-center">Status {renderSortArrows('Status')}</div>
                    </th>
                    <th className="px-4 py-3 cursor-pointer" onClick={() => handleSort('InventoryFlag')}>
                      <div className="flex items-center">Inventory Flag {renderSortArrows('InventoryFlag')}</div>
                    </th>
                    <th className="px-4 py-3 text-center">Actions</th>
                  </tr>
                </thead>

                <tbody >
                  {currentItems.map((stockIn) => {
                    const isActive = stockIn.Status === 1 || stockIn.Status === true;
                    const statusText = isActive ? 'Active' : 'Inactive';
                    const hasInventoryFlag = stockIn.InventoryFlag === 1 || stockIn.InventoryFlag === true;
                    const inventoryFlagText = hasInventoryFlag ? 'Yes' : 'No';

                    return (
                      <tr key={stockIn.StockInID}>
                        <td className="px-4 py-3 text-left font-medium uppercase" style={{ color: COLORS.TEXT , fontSize: '0.825rem' , fontWeight:'500' }}>{stockIn.StockInID}</td>
                        <td className="px-4 py-3 text-left uppercase" style={{ color: COLORS.MUTED, fontSize: '0.825rem' }}>{stockIn.StockInDate ? new Date(stockIn.StockInDate).toLocaleDateString() : '-'}</td>
                        <td className="px-4 py-3 text-left uppercase" style={{ color: COLORS.MUTED , fontSize: '0.825rem'}}>{stockIn.AddedByUserName || '-'}</td>
                        <td className="px-4 py-3 text-left" style={{ color: COLORS.MUTED, fontSize: '0.825rem' }}>{stockIn.TotalQuantity}</td>
                        <td className="px-4 py-3 uppercase text-left" style={{ color: COLORS.MUTED , fontSize: '0.825rem'}}>{stockIn.Note || '-'}</td>
                        <td className="px-4 py-3 text-left">
                          <button
                            className= {`  rounded-full ${isActive ? 'stock-status bg-teal-50 text-teal-700 font-bold hover:bg-teal-100' : 'stock-status-in bg-amber-50 font-bold text-amber-700 hover:bg-amber-100 '}`}
                            onClick={() => toggleStockInStatus(stockIn.StockInID, isActive)}
                            title={`Click to ${isActive ? 'deactivate' : 'activate'}`}
                            
                          >
                            {statusText}
                          </button>
                        </td>
                        <td className="px-4 py-3">
                          <span className="inventory-badge inline-block px-3 py-1 text-xs font-semibold uppercase" style={{ background: COLORS.BADGE_BG, color: hasInventoryFlag ? COLORS.TEXT : COLORS.MUTED }}>
                            {inventoryFlagText}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex justify-center space-x-4" style={{ color: COLORS.MUTED }}>
                            {canView && (
                              <button onClick={() => handleView(stockIn)} title="View" style={{ color: COLORS.TEXT }} className="hover:opacity-90">
                                <FaEye />
                              </button>
                            )}
                            {canEdit && (
                              <button onClick={() => handleEdit(stockIn)} title="Edit" className="hover:opacity-90" style={{ color: COLORS.BABY1 }}>
                                <FaEdit />
                              </button>
                            )}
                            <button onClick={() => downloadStockinPDF(stockIn.StockInID)} title="Download PDF" className="hover:opacity-90" style={{ color: COLORS.TEXT }}>
                              <FaDownload />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
        </section>

        {/* Stock In Form Modal */}
        <StockInFormModal
          open={showModal}
          onClose={() => setShowModal(false)}
          onSubmit={handleModalSubmit}
          initialData={selectedStockIn}
          isEdit={!!selectedStockIn}
        />

        <StockInViewModal
          open={viewModalOpen}
          onClose={() => setViewModalOpen(false)}
          stockInId={selectedStockIn}
        />

        {/* PDF Designer Modal */}
        {isPDFDesignerOpen && selectedStockinForPDF && (
          <PDFDesigner
            isOpen={isPDFDesignerOpen}
            onClose={() => setIsPDFDesignerOpen(false)}
            entity="Stock In"
            sampleData={{
              StockInID: selectedStockinForPDF.StockInID || selectedStockinForPDF.stockInId || '-',
              StockInDate: selectedStockinForPDF.StockInDate ? new Date(selectedStockinForPDF.StockInDate).toLocaleDateString() : '-',
              AddedByUserID: selectedStockinForPDF.AddedByUserID || selectedStockinForPDF.addedByUserId || '-',
              AddedByUserName: selectedStockinForPDF.AddedByUserName || selectedStockinForPDF.addedByUserName || '-',
              BranchCode: selectedStockinForPDF.BranchCode || selectedStockinForPDF.branchCode || '-',
              BranchName: selectedStockinForPDF.BranchName || selectedStockinForPDF.branchName || '-',
              Status: selectedStockinForPDF.Status === 1 || selectedStockinForPDF.Status === true ? 'Active' : 'Inactive',
              InventoryFlag: selectedStockinForPDF.InventoryFlag === 1 || selectedStockinForPDF.InventoryFlag === true ? 'Yes' : 'No',
              TotalQuantity: selectedStockinForPDF.TotalQuantity || selectedStockinForPDF.totalQuantity || 0,
              Note: selectedStockinForPDF.Note || selectedStockinForPDF.note || '-',
              Items: (selectedStockinForPDF.Items || []).map(item => ({
                ItemCode: item.ItemCode || item.itemCode || '-',
                ItemName: item.ItemName || item.itemName || '-',
                UOM: item.UOM || item.uom || '-',
                QuantityAdded: item.QuantityAdded || item.quantityAdded || 0,
                Rate: item.Rate || item.rate || 0,
                Value: item.Value || item.value || 0,
                Warehouse: item.LocationCode || item.locationCode || '-',
              })),
              Summary: {
                TotalQuantity: selectedStockinForPDF.TotalQuantity || selectedStockinForPDF.totalQuantity || 0,
                TotalValue: (selectedStockinForPDF.Items || []).reduce((sum, i) => sum + (i.Value || i.value || 0), 0)
              }
            }}
            availableFields={{
              Header: [
                "StockInID", "StockInDate", "AddedByUserID", "AddedByUserName",
                "BranchCode", "BranchName", "Status", "InventoryFlag",
                "TotalQuantity", "Note"
              ],
              Items: [
                "ItemCode", "ItemName", "UOM", "QuantityAdded",
                "Rate", "Value", "Warehouse"
              ],
              Summary: [
                "TotalQuantity", "TotalValue"
              ]
            }}
          />
        )}

      </main>
    </div>
  );
};

export default StockInItems;
